import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { ApiBaseService } from "../services/api-base.service";
import {
  ProjectGetBoardListResponse,
  ProjectGetBoardResponse,
  ProjectHubBoardListItem,
  ProjectHubSpark,
  GetSparkBoardRequest,
  GetSparkBoardResponse,
  GetSparkAnswersRequest,
  GetSparkAnswersResponse,
  WriteSparkAnswerRequest,
  WriteSparkRequestModel,
  GetSparkRequest,
  GetSparkResponse,
  SparkListItem,
  SparkAnswerItem as SparkAnswer,
  SparkBoardFilterState,
  LikeAnswerRequest,
  ReactToSparkRequest,
} from "../pages/grid-connection/project/project-tabs/project-hub/project-hub.types";
import { GenericResultModel } from "../interfaces/entities/base";

@Injectable({
  providedIn: "root",
})
export class ProjectHubService {

  
  private _newSparksMap$ = new BehaviorSubject<{ [boardId: number]: number }>({});
  public get newSparksMap$(): Observable<{ [boardId: number]: number }> {
    return this._newSparksMap$.asObservable();
  }
  public setNewSparksMap(map: { [boardId: number]: number }) {
    this._newSparksMap$.next(map);
  }
  // Board state management
  public boardsSubject = new BehaviorSubject<ProjectHubBoardListItem[]>([]);

  public sparkDeletedSubject = new Subject<void>();

  constructor(private apiService: ApiBaseService) {
    this.newSparksMap$.subscribe(map => {
      console.log('newSparksMap changed:', map);
    });
  }

  async GetBoardList(projectId: number): Promise<ProjectGetBoardListResponse> {
    const result = await this.apiService.httpRequest(
      "ProjectHubService/GetBoardList",
      { projectId },
      true,
      true,
    );
    if (!result.hasError) {
      this.boardsSubject.next(result.boards);
    }
    return result;
  }

  async WriteBoard(params: {
    board: ProjectHubBoardListItem;
    projectId: number;
  }): Promise<any> {
    return this.apiService.httpRequest(
      "ProjectHubService/WriteBoard",
      params,
      true,
      true,
    );
  }

  async GetBoard(
    boardId: number,
    pageNumber: number = 1,
    pageSize: number = 5,
    filters: SparkBoardFilterState,
    searchTerm?: string,
  ): Promise<ProjectGetBoardResponse> {
    const params: any = {
      SparkBoardId: boardId,
      PageNumber: pageNumber,
      PageSize: pageSize,
      searchTerm,
      filters: {
        periodFrom: filters.period.from,
        periodTo: filters.period.to,
        tags: filters.tags || [],
        person: filters.person,
        unread: filters.unread || false,
      },
    };

    return this.apiService.httpRequest(
      "ProjectHubService/GetSparkBoard",
      params,
      true,
      true,
    );
  }

  async WriteSpark(boardId: number, spark: ProjectHubSpark): Promise<any> {
    const formData = new FormData();

    // Create the WriteSparkRequestModel structure
    const requestModel: WriteSparkRequestModel = {
      boardId: boardId,
      spark: {
        id: spark.id,
        userEmail: spark.userEmail,
        content: {
          text: spark.content.text,
          files: Object.keys(spark.content.files).reduce(
            (acc, key) => {
              acc[key] = {
                fileName: spark.content.files[key].fileName,
                fileType: spark.content.files[key].fileType,
              };
              return acc;
            },
            {} as { [key: string]: { fileName: string; fileType: string } },
          ),
        },
        dateCreated: spark.dateCreated,
        tags: spark.tags,
        pinned: spark.pinned,
        isDeleted: spark.isDeleted,
      },
    };

    // Append the data field as JSON string
    formData.append("data", JSON.stringify(requestModel));

    // Append files with their corresponding keys (file IDs)
    if (spark.content.files) {
      Object.keys(spark.content.files).forEach((fileId) => {
        if (spark.content.files[fileId].file) {
          formData.append(
            fileId,
            spark.content.files[fileId].file,
            spark.content.files[fileId].file.name,
          );
        }
      });
    }

    return this.apiService.httpRequestForm(
      "ProjectHubService/WriteSpark",
      formData,
      true,
      true,
    );
  }

  async GetSpark(sparkId: number): Promise<GetSparkResponse> {
    const request: GetSparkRequest = { sparkId };
    return this.apiService.httpRequest(
      "ProjectHubService/GetSpark",
      request,
      true,
      true,
    );
  }

  // Comment-related methods
  async GetSparkBoard(
    sparkBoardId: number,
    pageNumber: number = 1,
    pageSize: number = 5,
    searchTerm?: string,
  ): Promise<GetSparkBoardResponse> {
    const request: GetSparkBoardRequest = {
      sparkBoardId,
      pageNumber,
      pageSize,
    };
    if (searchTerm) {
      request.searchTerm = searchTerm;
    }
    return this.apiService.httpRequest(
      "ProjectHubService/GetSparkBoard",
      request,
      true,
      true,
    );
  }

  async GetSparkAnswers(sparkId: number): Promise<GetSparkAnswersResponse> {
    const request: GetSparkAnswersRequest = { sparkId };
    return this.apiService.httpRequest(
      "ProjectHubService/GetSparkAnswers",
      request,
      true,
      false,
    );
  }

  async WriteSparkAnswer(
    sparkId: number,
    answer: {
      id?: number;
      content?: string;
      isDeleted?: boolean;
    },
  ): Promise<GenericResultModel> {
    const request: WriteSparkAnswerRequest = { sparkId, answer };
    return this.apiService.httpRequest(
      "ProjectHubService/WriteSparkAnswer",
      request,
      true,
      true,
    );
  }

  async GetSparkAttachment(fileId: string): Promise<Blob> {
    const request = { fileId };
    return this.apiService.httpGETRequest(
      "DownloadService/GetSparkAttachmentFileContent",
      request,
      false,
      "blob",
    );
  }

  // Spark Tag API methods
  async getSparkTags(p: any) {
    const result = await this.apiService.httpRequest(
      "ProjectHubService/GetSparkTags",
      p,
      true,
      false,
    );
    const emailTag = result.sparkTags.find((tag) => tag.id === 1);
    if (emailTag) {
      emailTag.name = $localize`:@@email:Email`;
    }
    return result;
  }

  writeSparkTags(p: any) {
    return this.apiService.httpRequest(
      "ProjectHubService/WriteSparkTag",
      p,
      true,
      true,
    );
  }

  // Spark interaction methods
  async MarkSparkAsRead(sparkId: number): Promise<GenericResultModel> {
    const request = { sparkId };
    return this.apiService.httpRequest(
      "ProjectHubService/MarkSparkAsRead",
      request,
      true,
      false,
    );
  }

  async MarkAllSparksAsRead(boardId: number): Promise<GenericResultModel> {
    const request = { boardId };
    return this.apiService.httpRequest(
      "ProjectHubService/MarkAllSparksAsRead",
      request,
      true,
      false,
    );
  }

  async LikeSpark(
    sparkId: number,
    isLiked: boolean,
  ): Promise<GenericResultModel> {
    const request = { sparkId, isLiked };
    return this.apiService.httpRequest(
      "ProjectHubService/LikeSpark",
      request,
      true,
      false,
    );
  }

  async LikeAnswer(
    answerId: number,
    isLiked: boolean,
  ): Promise<GenericResultModel> {
    const request: LikeAnswerRequest = { answerId, isLiked };
    return this.apiService.httpRequest(
      "ProjectHubService/LikeAnswer",
      request,
      true,
      false,
    );
  }

  // Move spark to another board
  async MoveSpark(
    sparkId: number,
    targetBoardId: number,
  ): Promise<GenericResultModel> {
    const request = { sparkId, targetBoardId };
    return this.apiService.httpRequest(
      "ProjectHubService/MoveSpark",
      request,
      true,
      true,
    );
  }

  // React to spark with emoji
  async ReactToSpark(
    sparkId: number,
    reaction: string,
    isAdding: boolean,
  ): Promise<GenericResultModel> {
    const request: ReactToSparkRequest = { sparkId, reaction, isAdding };
    return this.apiService.httpRequest(
      "ProjectHubService/ReactToSpark",
      request,
      true,
      false,
    );
  }
}
